/**
 * StatusPill — canonical single-tone status badge for sysadmin pages.
 *
 * Per SYSADMIN-PLATFORM-SPEC §11.12 ("Color usage"):
 *   - Single semantic palette across the sysadmin app.
 *   - Emerald + green-500 cannot both exist.
 *   - Destructive red reserved for: AlertDialog confirm, suspended/deleted
 *     status, audit outcome=failure.
 *
 * Replaces every ad-hoc inline `Badge variant=…` + `border-green-500/30
 * bg-green-500/5 text-green-600` pattern that grew up across audit.*,
 * runtime.*, tenants.*, catalog.* pages.
 *
 * @example
 * <StatusPill status="healthy">Healthy</StatusPill>
 * <StatusPill status="warning" icon={AlertTriangle}>Degraded</StatusPill>
 * <StatusPill status="destructive" icon={XCircle}>Down</StatusPill>
 * <StatusPill status="muted">Unknown</StatusPill>
 * <StatusPill status="info" icon={Clock}>Pending</StatusPill>
 */

import type { ComponentType } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type StatusPillStatus =
  | 'healthy'        // success / OK / active
  | 'warning'        // degraded / pending / warning
  | 'destructive'    // down / failed / suspended / deleted
  | 'info'           // informational (blue) — e.g. "in progress"
  | 'muted'          // unknown / disabled / inactive
  | 'neutral';       // generic outline (no tone)

export interface StatusPillProps {
  status: StatusPillStatus;
  children: React.ReactNode;
  icon?: LucideIcon | ComponentType<{ className?: string }>;
  /** Render as a compact tag (smaller padding + text) */
  compact?: boolean;
  className?: string;
}

const TONE: Record<StatusPillStatus, string> = {
  healthy:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  warning:
    'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300',
  destructive:
    'border-destructive/30 bg-destructive/10 text-destructive',
  info:
    'border-primary/30 bg-primary/10 text-primary',
  muted:
    'border-muted bg-muted/40 text-muted-foreground',
  neutral:
    'border-border bg-background text-foreground',
};

export function StatusPill({
  status,
  children,
  icon: Icon,
  compact = false,
  className,
}: StatusPillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border font-medium whitespace-nowrap',
        compact ? 'px-1.5 py-0 text-[10px]' : 'px-2 py-0.5 text-xs',
        TONE[status],
        className,
      )}
    >
      {Icon ? (
        <Icon className={cn(compact ? 'h-2.5 w-2.5' : 'h-3 w-3')} aria-hidden />
      ) : null}
      {children}
    </span>
  );
}
